/**
 * @module @repo/global-login-sdk/client
 * Erzeugt den geteilten Supabase-Cookie-Client für Cross-Subdomain-SSO.
 * Siehe docs/decisions/ADR-0008 (@supabase/ssr createBrowserClient im SPA).
 */
import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';
import { type GlobalLoginConfig, DEFAULT_COOKIE_DOMAIN } from './config';

/**
 * Prüft, ob der aktuelle Host eine lokale Entwicklungsumgebung ist
 * (`localhost`, Loopback-IPs oder `*.localhost`). Außerhalb des Browsers `false`.
 *
 * @returns `true`, wenn auf einem localhost-artigen Host ausgeführt.
 */
function isLocalhostHost(): boolean {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.localhost')
  );
}

/**
 * Erzeugt den zentralen Auth-Client mit Cookie-Storage auf `.gmolike.me`,
 * sodass eine Session über alle Subdomains hinweg gilt ("einmal einloggen,
 * überall eingeloggt"). PKCE-Flow; `networkMode: 'always'` wird im QueryClient
 * der jeweiligen App gesetzt (nicht hier).
 *
 * Lokal (localhost) wird das `domain`-Attribut bewusst WEGGELASSEN: ein
 * Cross-Subdomain-Cookie wie `.gmolike.me` ist auf localhost ungültig und wird
 * vom Browser verworfen → die Session würde nie persistieren und Login/Admin
 * würden sich gegenseitig endlos weiterleiten. Ein host-only Cookie gilt auf
 * localhost portübergreifend (Ports sind für Cookies nicht relevant). `secure`
 * folgt dem Protokoll, damit das Cookie auch über `http://localhost` gesetzt wird.
 *
 * @param cfg App-spezifische Login-Konfiguration.
 * @returns Ein konfigurierter Supabase-Client mit subdomain-weiter Session.
 */
export function createGlobalLoginClient(cfg: GlobalLoginConfig): SupabaseClient {
  const local = isLocalhostHost();
  const configuredDomain = cfg.cookieDomain ?? DEFAULT_COOKIE_DOMAIN;
  // Host-only auf localhost (oder bei explizit leerer Domain), sonst Cross-Subdomain.
  const domain = local || !configuredDomain ? undefined : configuredDomain;
  const secure = typeof window === 'undefined' ? true : window.location.protocol === 'https:';

  return createBrowserClient(cfg.supabaseUrl, cfg.publishableKey, {
    cookieOptions: {
      ...(domain ? { domain } : {}),
      path: '/',
      sameSite: 'lax',
      secure,
    },
    auth: { flowType: 'pkce' },
  });
}
